import type {
  VoiceRealtimeClientEvent,
  VoiceResponseCreateTrigger,
} from "@/types/voice-chat-rtc"
import uniq from "lodash/uniq"

import {
  voiceTracer,
  type VoiceTraceEventName,
} from "@/lib/voice-trace/voice-trace"

const MANUAL_RESPONSE_DONE_FALLBACK_DELAY_MS = 300
// How long a deferred pause resume waits for the cancelled response to report
// done before the turn is settled honestly instead. The cancel round trip runs
// in tens of milliseconds (~110ms in the field traces); this only has to
// outlast a slow one, and expiring is strictly better than a frozen caption.
const PAUSE_RESUME_LOCK_WAIT_MS = 1_500
// Two creates, then settle. A second rejection means the lock is not being
// released on the timeline the first rejection implied.
const PAUSE_RESUME_MAX_ATTEMPTS = 2
// A result narration that played this long counts as heard: the customer has
// been given enough of the result to ask for the rest themselves, and the
// function call output stays in context to answer that ask. Below it they
// heard at most a name, so the follow-up still owes them the results.
const INTERRUPTED_RESULT_RETRY_MAX_PLAYED_MS = 2_000
const gateTrace = voiceTracer("gate")

type CustomerTurnPhase = "idle" | "speaking" | "awaiting_automatic_response"

interface ReadyToolResult {
  itemId: string
  callId: string
  customerSpeechGeneration: number
  publish(): boolean
}

interface ToolResultItem {
  itemId: string
  callId: string
  acknowledged: boolean
}

interface FixedToolResultDelivery {
  toolResultItems: ToolResultItem[]
  retryCallIds: string[]
  retryMarkerSent: boolean
  generation: number
}

interface ResponseCreateInFlight {
  eventId: string
  trigger: VoiceResponseCreateTrigger
  toolResultItems: ToolResultItem[]
  retryCallIds: string[]
  generation: number
}

interface CurrentResultResponse {
  responseId: string
  callIds: string[]
}

interface ResponseGateDeps {
  send(event: VoiceRealtimeClientEvent): boolean
  isAudioBufferActive(): boolean
  // The resume the pause promised can never be delivered. The engine is
  // holding a dimmed tail nothing is going to speak, so it has to settle that
  // turn rather than leave it frozen mid-answer.
  onPauseResumeUndeliverable(): void
}

/**
 * Serializes manual responses because Realtime allows one active response.
 * Tool results remain local until the customer, response, and playback
 * lifecycles reach a safe publication point. A fixed delivery owns published
 * result membership until its matching response.create is accepted.
 */
export class ResponseGate {
  private activeRealtimeResponseId: string | null = null
  private responseCreateInFlight: ResponseCreateInFlight | null = null
  private customerTurnPhase: CustomerTurnPhase = "idle"
  private readonly readyToolResults = new Map<string, ReadyToolResult>()
  private readonly pendingResultRetryCallIds = new Set<string>()
  private fixedToolResultDelivery: FixedToolResultDelivery | null = null
  private currentResultResponse: CurrentResultResponse | null = null
  private customerSpeechGeneration = 0
  private readonly responseDoneFallbackTimers = new Map<
    string,
    { timer: number; generation: number }
  >()
  private readonly staleResponseCreateBatches = new Map<
    string,
    ResponseCreateInFlight
  >()
  private readonly staleProviderResponseIds = new Set<string>()
  private interruptionGeneration = 0
  private responseCreateEventCounter = 0
  // While the session is paused nothing may start the assistant speaking: tool
  // results that land keep queuing, but no response.create leaves until resume.
  private paused = false
  // A resume waiting on Realtime's one-active-response lock. The pause's
  // `response.cancel` is a round trip, so a customer who taps resume before it
  // lands has their create rejected with
  // `conversation_already_has_active_response` — and a dropped `pause_resume`
  // left the answer frozen behind a dimmed tail that nothing would ever speak
  // and no pause, resume, or speech could clear.
  private pendingPauseResume = false
  private pauseResumeAttempts = 0
  private pauseResumeTimer: number | null = null
  // Result call ids whose narration the pause's own clear cut off. They retire
  // when Realtime accepts the continuation that owns re-speaking them; an
  // abandoned or empty continuation promotes them to the retry set.
  private readonly pausedResultRetryCallIds = new Set<string>()

  constructor(private deps: ResponseGateDeps) {}

  get activeResponseId() {
    return this.activeRealtimeResponseId
  }

  get currentCustomerSpeechGeneration() {
    return this.customerSpeechGeneration
  }

  isResponseSuppressed(responseId: string) {
    return this.staleProviderResponseIds.has(responseId)
  }

  // Returns the tool call ids represented by this response. VoiceChatRTC gives
  // them to the first nonblank assistant item, which may be persisted only
  // after playback stops or an interruption determines the audible prefix.
  // Realtime echoes trigger metadata but not the client response.create event ID.
  // Serialization guarantees at most one unresolved local batch can match it.
  responseCreated(responseId: string, trigger?: VoiceResponseCreateTrigger) {
    const staleBatch = [...this.staleResponseCreateBatches.values()].find(
      (batch) => batch.trigger === trigger
    )
    if (staleBatch) {
      this.staleResponseCreateBatches.delete(staleBatch.eventId)
      this.staleProviderResponseIds.add(responseId)
      this.deps.send({ type: "response.cancel", response_id: responseId })
      this.deps.send({ type: "output_audio_buffer.clear" })
      gateTrace.info(
        "response.cancel",
        {
          eventId: staleBatch.eventId,
          responseId,
          trigger: staleBatch.trigger,
        },
        "out"
      )
      return []
    }

    if (trigger === undefined) {
      this.customerTurnPhase = "idle"
    }
    this.activeRealtimeResponseId = responseId
    const inFlight = this.responseCreateInFlight
    if (!inFlight || inFlight.trigger !== trigger) {
      return []
    }

    gateTrace.info(
      "tool_result_response_acknowledged",
      {
        eventId: inFlight.eventId,
        responseId,
        trigger: inFlight.trigger,
        toolResultItemIds: inFlight.toolResultItems.map(({ itemId }) => itemId),
        retryCallIds: inFlight.retryCallIds,
      },
      "in"
    )
    this.responseCreateInFlight = null
    if (inFlight.trigger === "pause_resume") {
      this.pausedResultRetryCallIds.clear()
    }
    if (inFlight.trigger === "tool_result") {
      this.currentResultResponse = {
        responseId,
        callIds: [
          ...inFlight.retryCallIds,
          ...inFlight.toolResultItems.map(({ callId }) => callId),
        ],
      }
    }
    return inFlight.toolResultItems.map(({ callId }) => callId)
  }

  // Terminal tool results the hold is keeping unspoken — the queue a resume
  // would otherwise make wait through the whole re-spoken remainder.
  hasUndeliveredToolResults() {
    return (
      this.readyToolResults.size > 0 ||
      (this.fixedToolResultDelivery?.toolResultItems.length ?? 0) > 0
    )
  }

  responseDone(responseId: string | null, noAudio = false, cancelled = false) {
    this.releaseFinishedResponse(responseId, noAudio, cancelled)
    // Whatever held the lock has reported done — including a cancelled
    // response, which is exactly what a deferred resume is waiting for.
    if (this.pendingPauseResume) {
      this.deliverPauseResume()
    }
  }

  private releaseFinishedResponse(
    responseId: string | null,
    noAudio: boolean,
    cancelled: boolean
  ) {
    if (responseId && this.staleProviderResponseIds.delete(responseId)) return

    if (!responseId) {
      this.activeRealtimeResponseId = null
      return
    }
    if (this.activeRealtimeResponseId === responseId) {
      this.activeRealtimeResponseId = null
    }

    if (noAudio && this.currentResultResponse?.responseId === responseId) {
      if (cancelled) {
        const retrySet = this.paused
          ? this.pausedResultRetryCallIds
          : this.pendingResultRetryCallIds
        this.currentResultResponse.callIds.forEach((callId) => {
          retrySet.add(callId)
        })
      }
      this.currentResultResponse = null
    }

    // Generation completion releases Realtime's response lock, but audio may
    // begin later. This timer retries progression only; it does not prove audio
    // is absent.
    this.armResponseDoneFallbackTimer(responseId)
  }

  audioStopped(responseId: string) {
    this.clearResponseDoneFallbackTimers()
    if (this.currentResultResponse?.responseId === responseId) {
      this.currentResultResponse = null
    }
    this.flushPendingResponse()
  }

  audioCleared(responseId: string, playedMs: number) {
    this.clearResponseDoneFallbackTimers()
    this.interruptionGeneration += 1

    if (this.currentResultResponse?.responseId === responseId) {
      // A pause's clear is not (yet) a lost narration: the resume re-speaks the
      // cut remainder verbatim, so the customer DOES hear these results, and
      // enrolling them here made the interrupted-result follow-up fire after
      // the continuation finished — the assistant narrated the same results a
      // second time. They are parked rather than dropped because only the
      // pause's ENDING settles it: resume discards them, speech promotes them.
      if (playedMs < INTERRUPTED_RESULT_RETRY_MAX_PLAYED_MS) {
        const retrySet = this.paused
          ? this.pausedResultRetryCallIds
          : this.pendingResultRetryCallIds
        this.currentResultResponse.callIds.forEach((callId) => {
          retrySet.add(callId)
        })
      }
      this.currentResultResponse = null
    }

    this.reopenFixedDeliveryRetries()

    const activeResponseId = this.activeRealtimeResponseId
    if (activeResponseId) {
      this.staleProviderResponseIds.add(activeResponseId)
      this.activeRealtimeResponseId = null
    }
    const inFlight = this.responseCreateInFlight
    if (inFlight) {
      if (inFlight.trigger === "tool_result") {
        const interruptedCallIds = [
          ...inFlight.retryCallIds,
          ...inFlight.toolResultItems.map(({ callId }) => callId),
        ]
        interruptedCallIds.forEach((callId) => {
          this.pendingResultRetryCallIds.add(callId)
        })
      }
      this.staleResponseCreateBatches.set(inFlight.eventId, inFlight)
      this.responseCreateInFlight = null
    }
  }

  toolResultReady(result: ReadyToolResult) {
    this.readyToolResults.set(result.itemId, result)
    gateTrace.info(
      "tool_result_response_queued",
      {
        activeResponseId: this.activeRealtimeResponseId,
        inFlightEventId: this.responseCreateInFlight?.eventId ?? null,
        itemId: result.itemId,
        customerSpeechGeneration: result.customerSpeechGeneration,
      },
      "out"
    )
    this.flushPendingResponse()
  }

  toolResultItemAcknowledged(itemId: string) {
    const delivery = this.fixedToolResultDelivery
    const item = delivery?.toolResultItems.find(
      (toolResultItem) => toolResultItem.itemId === itemId
    )
    if (!item) {
      return
    }

    item.acknowledged = true
    this.flushPendingResponse()
  }

  toolResultItemRejected(itemId: string) {
    const delivery = this.fixedToolResultDelivery
    if (delivery) {
      delivery.toolResultItems = delivery.toolResultItems.filter(
        (item) => item.itemId !== itemId
      )
      if (
        delivery.toolResultItems.length === 0 &&
        delivery.retryCallIds.length === 0
      ) {
        this.fixedToolResultDelivery = null
      }
    }
    this.staleResponseCreateBatches.forEach((batch, eventId) => {
      if (batch.trigger !== "tool_result") return

      batch.toolResultItems = batch.toolResultItems.filter(
        (item) => item.itemId !== itemId
      )
      if (
        batch.toolResultItems.length === 0 &&
        batch.retryCallIds.length === 0
      ) {
        this.staleResponseCreateBatches.delete(eventId)
      }
    })
    this.flushPendingResponse()
  }

  handleError(event: {
    event_id?: string
    code?: string
    error?: { code?: string; event_id?: string }
  }) {
    // A cancel can race a terminal response.done during barge-in or manual stop.
    // The provider has already reached the requested state, so the error is inert.
    if ((event.code ?? event.error?.code) === "response_cancel_not_active") {
      gateTrace.info(
        "response_cancel_not_active_ignored",
        { serverEventId: event.event_id },
        "in"
      )
      return true
    }

    const rejectedEventId = event.error?.event_id
    if (
      (event.code ?? event.error?.code) !==
        "conversation_already_has_active_response" ||
      !rejectedEventId
    ) {
      return false
    }

    const staleBatch = this.staleResponseCreateBatches.get(rejectedEventId)
    if (staleBatch) {
      this.staleResponseCreateBatches.delete(rejectedEventId)
      return true
    }

    const inFlight = this.responseCreateInFlight
    if (rejectedEventId !== inFlight?.eventId) return false

    gateTrace.warn(
      "tool_result_response_rejected_active_response",
      {
        clientEventId: rejectedEventId,
        serverEventId: event.event_id,
        trigger: inFlight.trigger,
        toolResultItemIds: inFlight.toolResultItems.map(({ itemId }) => itemId),
        retryCallIds: inFlight.retryCallIds,
      },
      "in"
    )
    this.responseCreateInFlight = null
    if (inFlight.trigger === "pause_resume") {
      // The cancel had not landed when the provider read this create. The
      // remainder is still held engine-side, so wait for the lock rather than
      // drop the resume on the floor.
      this.deferPauseResume()
      return true
    }
    if (
      inFlight.trigger === "tool_result" &&
      inFlight.generation === this.interruptionGeneration
    ) {
      this.fixedToolResultDelivery = {
        toolResultItems: inFlight.toolResultItems,
        retryCallIds: inFlight.retryCallIds,
        retryMarkerSent: inFlight.retryCallIds.length > 0,
        generation: inFlight.generation,
      }
    }
    return true
  }

  userSpeechStarted() {
    // Talking supersedes a resume still waiting for the lock: delivering it
    // now would speak the old remainder over the turn they just started.
    if (this.pendingPauseResume) {
      this.abandonPauseResume("customer_speech")
    }
    this.customerSpeechGeneration += 1
    this.customerTurnPhase = "speaking"

    this.reopenFixedDeliveryRetries()

    const inFlight = this.responseCreateInFlight
    if (
      inFlight?.trigger === "tool_result" &&
      inFlight.toolResultItems.length === 0 &&
      inFlight.retryCallIds.length > 0
    ) {
      inFlight.retryCallIds.forEach((callId) => {
        this.pendingResultRetryCallIds.add(callId)
      })
      this.staleResponseCreateBatches.set(inFlight.eventId, inFlight)
      this.responseCreateInFlight = null
    }
  }

  userSpeechStopped() {
    this.customerTurnPhase = "awaiting_automatic_response"
  }

  userTurnDiscarded() {
    this.customerTurnPhase = "idle"
    this.flushPendingResponse()
  }

  // An empty transcription after a real barge-in means noise cancelled the reply.
  // Resume only when no valid response or tool narration can move it forward.
  requestPhantomInterruptResume() {
    if (
      this.activeRealtimeResponseId ||
      this.responseCreateInFlight ||
      this.pendingResultRetryCallIds.size > 0 ||
      this.readyToolResults.size > 0 ||
      this.fixedToolResultDelivery ||
      this.staleResponseCreateBatches.size > 0 ||
      this.staleProviderResponseIds.size > 0
    ) {
      gateTrace.info(
        "phantom_interrupt_resume_skipped",
        {
          activeResponseId: this.activeRealtimeResponseId,
          inFlightEventId: this.responseCreateInFlight?.eventId ?? null,
          readyToolResultItemCount: this.readyToolResults.size,
        },
        "out"
      )
      return
    }

    this.sendResponseCreate(
      "phantom_interrupt_resume",
      "phantom_interrupt_resume_sent"
    )
  }

  // Holds every queued response while the user has the session paused; lifting
  // the hold flushes whatever a pause-resume continuation didn't claim first.
  setPaused(paused: boolean) {
    if (this.paused === paused) return
    this.paused = paused
    if (!paused) {
      this.flushPendingResponse()
    }
  }

  // The customer spoke while the session was paused, which ends the pause
  // (VoiceChatRTC.abandonPauseForUserSpeech). The hold drops WITHOUT the
  // flush: anything queued behind the pause would start speaking over the
  // customer's live turn, so it stays queued for the gate's normal speech
  // handling to place.
  abandonPause() {
    this.paused = false
    // No resume is coming to re-speak the remainder, so a narration the pause
    // cut off is now genuinely unheard: hand its call ids to the
    // interrupted-result follow-up, which is what covers still-relevant
    // results at the next safe point.
    this.pausedResultRetryCallIds.forEach((callId) => {
      this.pendingResultRetryCallIds.add(callId)
    })
    this.pausedResultRetryCallIds.clear()
  }

  // A resume whose continuation the engine dropped (the held remainder was
  // the search-wait filler and the results it was covering arrived during the
  // pause). Nothing re-speaks the remainder, so the pause settles exactly as
  // a customer-ended one — and the lifted hold flushes the queued results
  // immediately instead of behind a re-spoken wait.
  resumeWithoutContinuation() {
    this.abandonPause()
    this.flushPendingResponse()
  }

  // The resume continuation after a pause. Unguarded against the ordinary
  // gate conditions (unlike the phantom resume) because the remainder must be
  // spoken before any queued tool narration — sending first is what makes this
  // response own the lock the flush would otherwise take. The one thing it
  // does wait for is the provider's response lock.
  requestPauseResume() {
    this.pauseResumeAttempts = 0
    this.deliverPauseResume()
  }

  // Sent immediately, exactly as before — the provider almost always has the
  // lock free by the time a customer reaches for resume. What changed is the
  // rejection: a resume that beat the cancel's round trip is now re-sent from
  // `responseDone`, once the response that held the lock reports finished.
  private deliverPauseResume() {
    if (this.pauseResumeAttempts >= PAUSE_RESUME_MAX_ATTEMPTS) {
      this.abandonPauseResume("attempts_exhausted")
      return
    }
    this.pendingPauseResume = false
    this.clearPauseResumeTimer()
    this.pauseResumeAttempts += 1
    this.sendResponseCreate("pause_resume", "pause_resume_sent")
  }

  // The rejection itself is already traced (with this trigger) by the caller,
  // and the re-send traces as another `pause_resume_sent`.
  private deferPauseResume() {
    this.pendingPauseResume = true
    if (this.pauseResumeTimer !== null) return
    this.pauseResumeTimer = window.setTimeout(() => {
      this.pauseResumeTimer = null
      this.abandonPauseResume("lock_wait_expired")
    }, PAUSE_RESUME_LOCK_WAIT_MS)
  }

  // Nothing is going to speak the held remainder. Tell the engine so it can
  // settle the turn on the heard prefix its interrupted write already
  // persisted, instead of leaving a dimmed tail on screen forever.
  private abandonPauseResume(reason: string) {
    if (!this.pendingPauseResume) return
    this.pendingPauseResume = false
    this.clearPauseResumeTimer()
    this.pausedResultRetryCallIds.forEach((callId) => {
      this.pendingResultRetryCallIds.add(callId)
    })
    this.pausedResultRetryCallIds.clear()
    gateTrace.warn("pause_resume_abandoned", {
      reason,
      attempts: this.pauseResumeAttempts,
    })
    this.deps.onPauseResumeUndeliverable()
  }

  private clearPauseResumeTimer() {
    if (this.pauseResumeTimer === null) return
    window.clearTimeout(this.pauseResumeTimer)
    this.pauseResumeTimer = null
  }

  clearTimers() {
    this.clearResponseDoneFallbackTimers()
    this.clearPauseResumeTimer()
    this.pendingPauseResume = false
    this.staleResponseCreateBatches.clear()
    this.staleProviderResponseIds.clear()
  }

  private armResponseDoneFallbackTimer(responseId: string) {
    this.clearResponseDoneFallbackTimer(responseId)
    const generation = this.interruptionGeneration
    const timer = window.setTimeout(() => {
      this.responseDoneFallbackTimers.delete(responseId)
      if (generation === this.interruptionGeneration) {
        this.flushPendingResponse()
      }
    }, MANUAL_RESPONSE_DONE_FALLBACK_DELAY_MS)
    this.responseDoneFallbackTimers.set(responseId, { timer, generation })
  }

  private clearResponseDoneFallbackTimer(responseId: string) {
    const fallback = this.responseDoneFallbackTimers.get(responseId)
    if (!fallback) return

    window.clearTimeout(fallback.timer)
    this.responseDoneFallbackTimers.delete(responseId)
  }

  private clearResponseDoneFallbackTimers() {
    this.responseDoneFallbackTimers.forEach(({ timer }) => {
      window.clearTimeout(timer)
    })
    this.responseDoneFallbackTimers.clear()
  }

  private nextResponseCreateEventId() {
    this.responseCreateEventCounter += 1
    return `voice_response_create_${this.responseCreateEventCounter}`
  }

  private sendResponseCreate(
    trigger: VoiceResponseCreateTrigger,
    summary: VoiceTraceEventName
  ) {
    const eventId = this.nextResponseCreateEventId()
    const sent = this.deps.send({
      type: "response.create",
      event_id: eventId,
      response: {
        metadata: { mindtrip_trigger: trigger },
      },
    })
    if (!sent) return

    this.responseCreateInFlight = {
      eventId,
      trigger,
      toolResultItems: [],
      retryCallIds: [],
      generation: this.interruptionGeneration,
    }
    gateTrace.info(summary, { eventId, trigger, toolResultItemIds: [] }, "out")
  }

  // An interruption invalidates the marker but not published item
  // acknowledgements: retry IDs return to the pending set and the delivery
  // keeps its items. A retry-only delivery has nothing left and is dropped.
  private reopenFixedDeliveryRetries() {
    const delivery = this.fixedToolResultDelivery
    if (!delivery) return

    for (const callId of delivery.retryCallIds) {
      this.pendingResultRetryCallIds.add(callId)
    }
    if (delivery.toolResultItems.length > 0) {
      delivery.retryCallIds = []
      delivery.retryMarkerSent = false
      delivery.generation = this.interruptionGeneration
    } else {
      this.fixedToolResultDelivery = null
    }
  }

  private sendToolResultResponse(delivery: FixedToolResultDelivery) {
    const eventId = this.nextResponseCreateEventId()
    const sent = this.deps.send({
      type: "response.create",
      event_id: eventId,
      response: {
        metadata: { mindtrip_trigger: "tool_result" },
      },
    })
    if (!sent) {
      return
    }

    this.fixedToolResultDelivery = null
    this.responseCreateInFlight = {
      eventId,
      trigger: "tool_result",
      toolResultItems: delivery.toolResultItems,
      retryCallIds: delivery.retryCallIds,
      generation: delivery.generation,
    }
    gateTrace.info(
      "tool_result_response_flushed",
      {
        eventId,
        trigger: "tool_result",
        toolResultItemIds: delivery.toolResultItems.map(({ itemId }) => itemId),
        retryCallIds: delivery.retryCallIds,
      },
      "out"
    )
  }

  private flushPendingResponse() {
    if (
      this.paused ||
      this.customerTurnPhase !== "idle" ||
      this.activeRealtimeResponseId ||
      this.responseCreateInFlight ||
      this.responseDoneFallbackTimers.size > 0 ||
      this.staleResponseCreateBatches.size > 0 ||
      this.staleProviderResponseIds.size > 0 ||
      this.deps.isAudioBufferActive()
    ) {
      return
    }

    if (
      !this.fixedToolResultDelivery &&
      (this.pendingResultRetryCallIds.size > 0 ||
        this.readyToolResults.size > 0)
    ) {
      // Only the first-ready turn's results freeze together: a late result
      // from an earlier turn must not ride in a later turn's response, and a
      // ready answer is not held behind a late result for an older turn.
      const allReadyResults = [...this.readyToolResults.values()]
      const [frozenGeneration] = allReadyResults.map(
        (result) => result.customerSpeechGeneration
      )
      const readyResults = allReadyResults.filter(
        ({ customerSpeechGeneration }) =>
          customerSpeechGeneration === frozenGeneration
      )
      readyResults.forEach(({ itemId }) => {
        this.readyToolResults.delete(itemId)
      })
      const delivery: FixedToolResultDelivery = {
        toolResultItems: [],
        retryCallIds: [...this.pendingResultRetryCallIds],
        retryMarkerSent: false,
        generation: this.interruptionGeneration,
      }
      this.pendingResultRetryCallIds.clear()
      this.fixedToolResultDelivery = delivery
      for (const result of readyResults) {
        if (result.publish()) {
          delivery.toolResultItems.push({
            itemId: result.itemId,
            callId: result.callId,
            acknowledged: false,
          })
        }
      }
      if (
        delivery.toolResultItems.length === 0 &&
        delivery.retryCallIds.length === 0
      ) {
        this.fixedToolResultDelivery = null
        return
      }
    }

    const delivery = this.fixedToolResultDelivery
    if (delivery) {
      // A publication can begin a multi-item sequence. Its output must be
      // acknowledged before the marker and create can refer to its results.
      if (delivery.toolResultItems.some(({ acknowledged }) => !acknowledged)) {
        return
      }
      // IDs interrupted while waiting for acknowledgement join this delivery
      // so one marker names every interrupted call.
      if (this.pendingResultRetryCallIds.size > 0) {
        delivery.retryCallIds = uniq([
          ...delivery.retryCallIds,
          ...this.pendingResultRetryCallIds,
        ])
        this.pendingResultRetryCallIds.clear()
        delivery.retryMarkerSent = false
      }
      if (delivery.retryCallIds.length > 0 && !delivery.retryMarkerSent) {
        const markerSent = this.deps.send({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: `For the response immediately after this event: the response following tool calls ${JSON.stringify(delivery.retryCallIds)} was interrupted before the customer heard it. After answering the customer's latest request, present the still-relevant information from those results as if for the first time. Skip anything the conversation since then already covered, and add nothing else.${delivery.toolResultItems.length > 0 ? " Newer results were supplied with this event; the customer's latest request is answered from those." : ""}`,
              },
            ],
          },
        })
        if (!markerSent) {
          return
        }
        delivery.retryMarkerSent = true
      }

      this.sendToolResultResponse(delivery)
    }
  }
}
